import { Component, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { BehaviorSubject, firstValueFrom, Observable, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, pairwise, switchMap } from 'rxjs/operators';
import {
  EnterpriseSearchService,
  ServiceType,
  User,
  UserService
} from '@sabstravtech/obtservices/angular';
import { AddressForm, Traveller } from '@sabstravtech/obtservices/base';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';

import { DeviceDetector } from '../../../core/services/device-detector.service';
import { BaseComponent } from '../../../core/components/base_components/base-componet';
import { Helpers } from '../../../core/classes/helpers';

@Component({
  selector: 'app-ambulance-search',
  templateUrl: './ambulance-search.component.html',
  styleUrls: ['./ambulance-search.component.scss'],
  standalone: false
})
export class AmbulanceSearchComponent extends BaseComponent implements OnInit {
  ServiceType: typeof ServiceType = ServiceType;
  travellers: Traveller[];
  searchParams: any;
  isLocationLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  min_date = {
    year: moment().year(),
    month: moment().month() + 1,
    day: moment().date()
  };

  timesArray: string[] = [];
  isDefaultExtension = false;
  constructor(
    public deviceDetector: DeviceDetector,
    public searchService: EnterpriseSearchService,
    public title: Title,
    public translateService: TranslateService,
    private userService: UserService
  ) {
    super(title, translateService);
  }

  ngOnInit(): void {
    this.searchParams = this.searchService.searches[ServiceType.Ambulance];
    this.subscribe(this.searchService.travellerInformation, (travellers: Traveller[]) => {
      this.travellers = travellers;
    });
    this.generateTimesList();
    this.setupDefaultUserSubscriptions();
  }

  private setupDefaultUserSubscriptions(): void {
    // Subscribe to bobo changes to set default user data
    this.subscribe(this.userService.changeBobo, async (traveller: Traveller) => {
      await this.setDefaultUserData(traveller);
    });

    // When isLoading goes from true to false, the search completed and form was reset
    // Reload the default user data
    this.subscribe(
      this.searchParams.isLoading.pipe(
        pairwise(),
        filter(([prev, curr]) => prev === true && curr === false)
      ),
      () => {
        this.setDefaultUserData();
      }
    );
  }

  private generateTimesList(): void {
    for (let hour = 0; hour < 24; hour++) {
      for (let min = 0; min < 60; min += 15) {
        const hourStr = hour.toString().padStart(2, '0');
        const minStr = min.toString().padStart(2, '0');
        this.timesArray.push(`${hourStr}:${minStr}`);
      }
    }
  }

  formatter = (x: { name: string }) => x?.name || '';

  searchLocations = (text$: Observable<string>): Observable<any[]> => {
    const debouncedText$ = text$.pipe(debounceTime(200), distinctUntilChanged());
    return debouncedText$.pipe(
      switchMap(
        (term: string): Observable<any[]> =>
          term.length <= 2
            ? of([])
            : this.searchService.getCityLocations(term, 'GB', this.isLocationLoading)
      )
    );
  };

  searchPostCode = (text$: Observable<string>): Observable<any[]> => {
    const debouncedText$ = text$.pipe(debounceTime(1000), distinctUntilChanged());
    return debouncedText$.pipe(
      switchMap(
        (term: string): Observable<any[]> =>
          term.length < 2 ? of([]) : this.searchService.getPostcodeList(term, 'GB')
      )
    );
  };

  fromAddressChange(event) {
    this.searchParams.fromAddress = event;
  }

  toAddressChange(event) {
    this.searchParams.toAddress = event;
  }

  stationChange(event, isTo = false) {
    const newAddress = this.countyCheck(
      event,
      isTo ? this.searchParams.toAddress : this.searchParams.fromAddress
    );
    if (isTo) {
      this.searchParams.toAddress = newAddress;
    } else {
      this.searchParams.fromAddress = newAddress;
    }
  }

  countyCheck(event, currentAddress: AddressForm): AddressForm {
    const address = this.searchService.searches[ServiceType.Cab].getAddress(event);
    if (!address?.County && currentAddress?.County) {
      address.County = currentAddress.County;
    }
    return address;
  }
  ensureElementIsScrolledTo(event: any): void {
    try {
      const typeAheadList = event.target.nextElementSibling;
      const activeButton = typeAheadList.getElementsByClassName('active')[0];
      if (
        activeButton.offsetTop + activeButton.clientHeight >
        typeAheadList.clientHeight + typeAheadList.scrollTop
      ) {
        typeAheadList.scrollTop =
          activeButton.offsetTop + activeButton.clientHeight - typeAheadList.clientHeight;
      } else if (activeButton.offsetTop < typeAheadList.scrollTop) {
        typeAheadList.scrollTop = activeButton.offsetTop;
      }
    } catch (e) {
      console.log("Couldn't find elements to scroll");
    }
  }

  private async setDefaultUserData(traveller?: Traveller): Promise<void> {
    const currentUser = traveller || (await this.getCurrentUser());
    if (currentUser) {
      this.searchParams.dob = currentUser.dob
        ? Helpers.dateToNgDate(new Date(currentUser.dob))
        : null;
      const primaryPhoneNumber = (currentUser as User).phoneNumbers?.find(
        (phone) => phone.userPrimary || phone.officePrimary
      );
      this.searchParams.phoneNumber = primaryPhoneNumber
        ? [primaryPhoneNumber.countryCode, primaryPhoneNumber.areaCode, primaryPhoneNumber.number]
            .filter(Boolean)
            .join('')
        : '';
      if (primaryPhoneNumber?.extension) {
        this.isDefaultExtension = true;
        this.searchParams.phoneExtension = primaryPhoneNumber?.extension;
      }
    }
  }

  async getCurrentUser(): Promise<Traveller | User> {
    return this.userService.bobo ?? (await firstValueFrom(this.userService.getUser()));
  }
}
